import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  InsertDriveFile as GenericFileIcon,
  Image as ImageIcon,
  VideoLibrary as VideoIcon,
  Audiotrack as AudioIcon,
  TableChart as ExcelIcon,
  Article as WordIcon,
  Slideshow as PptIcon,
  Code as CodeIcon,
  FolderZip as ArchiveIcon,
  TextFields as TextIcon,
  Assessment as CsvIcon,
  DataObject as JsonIcon
} from '@mui/icons-material';
import DownloadIcon from '@mui/icons-material/Download';
import { base_logo_url } from '../../apis/constants';

const getFileTypeInfo = doc => {
  const fileType = doc.fileType?.toLowerCase() || '';
  const fileName = doc.name?.toLowerCase() || '';
  const filePath = doc.filePath?.toLowerCase() || '';

  const getExtension = () => {
    const sources = [fileName, filePath];
    for (const source of sources) {
      const match = source.match(/\.([a-z0-9]+)$/);
      if (match) return match[1].toLowerCase();
    }
    return '';
  };

  const extension = getExtension();

  if (
    fileType.startsWith('image/') ||
    ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'tif'].includes(
      extension
    )
  ) {
    return {
      icon: <ImageIcon sx={{ fontSize: 40 }} />,
      color: '#4CAF50',
      bgColor: '#E8F5E9',
      label: 'Image'
    };
  }

  if (fileType.includes('pdf') || extension === 'pdf') {
    return {
      icon: <PdfIcon sx={{ fontSize: 40 }} />,
      color: '#F44336',
      bgColor: '#FFEBEE',
      label: 'PDF'
    };
  }

  if (
    fileType.includes('excel') ||
    fileType.includes('spreadsheet') ||
    ['xls', 'xlsx', 'xlsm', 'xlsb', 'csv'].includes(extension)
  ) {
    return {
      icon: <ExcelIcon sx={{ fontSize: 40 }} />,
      color: '#2196F3',
      bgColor: '#E3F2FD',
      label: extension === 'csv' ? 'CSV' : 'Excel'
    };
  }

  if (
    fileType.includes('word') ||
    fileType.includes('document') ||
    ['doc', 'docx', 'docm', 'dot', 'dotx'].includes(extension)
  ) {
    return {
      icon: <WordIcon sx={{ fontSize: 40 }} />,
      color: '#2196F3',
      bgColor: '#E3F2FD',
      label: 'Word'
    };
  }

  if (
    fileType.includes('powerpoint') ||
    fileType.includes('presentation') ||
    ['ppt', 'pptx', 'pptm', 'pot', 'potx'].includes(extension)
  ) {
    return {
      icon: <PptIcon sx={{ fontSize: 40 }} />,
      color: '#FF9800',
      bgColor: '#FFF3E0',
      label: 'PowerPoint'
    };
  }

  if (
    fileType.includes('text') ||
    ['txt', 'rtf', 'md', 'log'].includes(extension)
  ) {
    return {
      icon: <TextIcon sx={{ fontSize: 40 }} />,
      color: '#795548',
      bgColor: '#EFEBE9',
      label: 'Text'
    };
  }

  if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
    return {
      icon: <ArchiveIcon sx={{ fontSize: 40 }} />,
      color: '#FF9800',
      bgColor: '#FFF3E0',
      label: 'Archive'
    };
  }

  if (
    fileType.startsWith('video/') ||
    ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv'].includes(extension)
  ) {
    return {
      icon: <VideoIcon sx={{ fontSize: 40 }} />,
      color: '#9C27B0',
      bgColor: '#F3E5F5',
      label: 'Video'
    };
  }

  if (
    fileType.startsWith('audio/') ||
    ['mp3', 'wav', 'aac', 'flac', 'ogg'].includes(extension)
  ) {
    return {
      icon: <AudioIcon sx={{ fontSize: 40 }} />,
      color: '#9C27B0',
      bgColor: '#F3E5F5',
      label: 'Audio'
    };
  }

  if (
    [
      'js',
      'jsx',
      'ts',
      'tsx',
      'html',
      'css',
      'py',
      'java',
      'cpp',
      'c',
      'php',
      'json',
      'xml'
    ].includes(extension)
  ) {
    return {
      icon: <CodeIcon sx={{ fontSize: 40 }} />,
      color: '#607D8B',
      bgColor: '#ECEFF1',
      label: extension.toUpperCase()
    };
  }

  if (extension === 'csv') {
    return {
      icon: <CsvIcon sx={{ fontSize: 40 }} />,
      color: '#4CAF50',
      bgColor: '#E8F5E9',
      label: 'CSV'
    };
  }

  if (extension === 'json') {
    return {
      icon: <JsonIcon sx={{ fontSize: 40 }} />,
      color: '#FF9800',
      bgColor: '#FFF3E0',
      label: 'JSON'
    };
  }

  if (extension === 'tiff' || extension === 'tif') {
    return {
      icon: <ImageIcon sx={{ fontSize: 40 }} />,
      color: '#4CAF50',
      bgColor: '#E8F5E9',
      label: 'TIFF'
    };
  }

  return {
    icon: <GenericFileIcon sx={{ fontSize: 40 }} />,
    color: '#757575',
    bgColor: '#FAFAFA',
    label: extension ? extension.toUpperCase() : 'File'
  };
};

const handleOpenFile = filePath => {
  window.open(filePath, '_blank');
};

export default function ShipmentDocuments({
  shipmentStatus,
  shipmentType,
  claimDetails,
  onPrintBOL,
  onPrintLabels,
  onDownloadPOD,
  podLoading
}) {
  const isActiveStatus =
    shipmentStatus === 'InTransit' ||
    shipmentStatus === 'Delivered' ||
    shipmentStatus === 'Booked';
  const showBOL = isActiveStatus && shipmentType !== 'Parcel';
  const showLabels = isActiveStatus;
  const showPOD = shipmentStatus === 'Delivered' && shipmentType === 'LTL';
  const claimDocs = claimDetails?.documents?.length > 0 ? claimDetails.documents : [];

  if (!showBOL && !showLabels && !showPOD && claimDocs.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 1.5,
        pt: 1.5,
        borderTop: '1px solid #f1f5f9'
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          color: '#94a3b8',
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
          mb: 0.6
        }}
      >
        Documents
      </Typography>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'nowrap',
          overflowX: 'auto',
          pb: 0.5,
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: '#cbd5e1',
            borderRadius: 4
          }
        }}
      >
        {showBOL && (
        <Button
          size='small'
          sx={{
            fontSize: 10,
            fontWeight: 600,
            p: '3px 10px',
            textTransform: 'none',
            color: '#2563eb',
            border: '1px solid #2563eb',
            borderRadius: '6px',
            '&:hover': { bgcolor: '#eff6ff' }
          }}
          onClick={onPrintBOL}
        >
          <DownloadIcon sx={{ fontSize: 13, mr: 0.4 }} />
          BOL
        </Button>
      )}

      {showLabels && (
        <Button
          size='small'
          sx={{
            fontSize: 10,
            fontWeight: 600,
            p: '3px 10px',
            textTransform: 'none',
            color: '#7c3aed',
            border: '1px solid #7c3aed',
            borderRadius: '6px',
            '&:hover': { bgcolor: '#f5f3ff' }
          }}
          onClick={onPrintLabels}
        >
          <DownloadIcon sx={{ fontSize: 13, mr: 0.4 }} />
          Labels
        </Button>
      )}

      {showPOD && (
        <Button
          size='small'
          sx={{
            fontSize: 10,
            fontWeight: 600,
            p: '3px 10px',
            textTransform: 'none',
            color: '#16a34a',
            border: '1px solid #16a34a',
            borderRadius: '6px',
            '&:hover': { bgcolor: '#ecfdf5' }
          }}
          onClick={onDownloadPOD}
          disabled={podLoading}
        >
          <DownloadIcon sx={{ fontSize: 13, mr: 0.4 }} />
          POD
        </Button>
      )}

      {claimDocs.slice(0, 4).map((doc, idx) => {
        const fileInfo = getFileTypeInfo(doc);
        return (
          <Box
            key={idx}
            onClick={() => handleOpenFile(base_logo_url + doc.filePath)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1,
              py: 0.4,
              borderRadius: '6px',
              border: `1px solid ${fileInfo.color}30`,
              bgcolor: fileInfo.bgColor,
              cursor: 'pointer',
              '&:hover': { boxShadow: 1 }
            }}
          >
            <Box
              sx={{
                color: fileInfo.color,
                display: 'flex',
                fontSize: 14
              }}
            >
              {fileInfo.icon}
            </Box>
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#1e293b' }}>
              {doc.name ? doc.name.substring(0, 18) : 'Doc'}
            </Typography>
          </Box>
        );
      })}
      </Box>
    </Box>
  );
}
